import React from 'react';
import Modal from '../common/Modal';
import ModalContent from '../common/ModalContent';
import { Button } from '../common';
import { Heading, Text, Typography } from '../common/Typography';
import { getGenderTextColor } from '../../utils/genderColors';
import { formatTournamentEntryLabel } from '../../utils/tournamentFormat';

const ROUND_LABELS = {
  round_of_16: 'Round of 16',
  quarter_final: 'Quarter Finals',
  semi_final: 'Semi Finals',
  final: 'Final'
};

/**
 * Modal for editing knockout matchups and regenerating bracket.
 * Consolidates Edit Matchup and Regenerate into one place.
 */
const EditKnockoutMatchupsModal = ({
  isOpen,
  onClose,
  matches,
  onEditMatch,
  onRegenerate,
  onRegenerateConfirm,
  regenerating = false
}) => {
  const knockoutMatches = (matches || []).filter((m) => m.round !== 'group');
  const byRound = {};
  ['round_of_16', 'quarter_final', 'semi_final', 'final'].forEach((r) => {
    byRound[r] = knockoutMatches.filter((m) => m.round === r).sort((a, b) => (a.match_order ?? 0) - (b.match_order ?? 0));
  });

  return (
    <Modal isOpen={isOpen} onClose={onClose} closeOnBackdropClick={!regenerating}>
      <ModalContent size="large" padding={false} className="flex min-h-0 flex-col overflow-hidden">
        <div className="p-6 border-b border-border-medium">
          <div className="flex items-center justify-between">
            <Heading level={2}>Edit Knockout Matchups</Heading>
            <button
              onClick={onClose}
              disabled={regenerating}
              className="text-text-muted hover:text-text-primary transition-colors disabled:opacity-50"
            >
              ✕
            </button>
          </div>
          <Text className="text-text-secondary mt-2 block">
            Change which teams play in each match, or regenerate the entire bracket from group standings.
          </Text>
        </div>

        <div className="flex-1 overflow-y-auto p-6 space-y-6">
          {['round_of_16', 'quarter_final', 'semi_final', 'final'].map((round) => {
            const roundMatches = byRound[round] || [];
            if (roundMatches.length === 0) return null;

            return (
              <div key={round}>
                <Typography variant="strong" className="mb-2 block">
                  {ROUND_LABELS[round]}
                </Typography>
                <div className="space-y-2">
                  {roundMatches.map((match) => (
                    <div
                      key={match.id}
                      className="flex items-center justify-between p-3 rounded-lg border border-border-medium bg-surface"
                    >
                      <div className="flex-1 min-w-0 flex items-center gap-2">
                        <span className="text-sm">
                          <span className={getGenderTextColor(match.team1?.player1?.gender || '')}>
                            {match.team1
                              ? formatTournamentEntryLabel(match.team1, !match.team1.player2)
                              : 'TBD'}
                          </span>
                        </span>
                        <span className="text-text-muted shrink-0">vs</span>
                        <span className="text-sm">
                          <span className={getGenderTextColor(match.team2?.player1?.gender || '')}>
                            {match.team2
                              ? formatTournamentEntryLabel(match.team2, !match.team2.player2)
                              : 'TBD'}
                          </span>
                        </span>
                      </div>
                      <Button
                        variant="secondary"
                        size="small"
                        onClick={() => onEditMatch(match)}
                        disabled={regenerating}
                      >
                        Edit
                      </Button>
                    </div>
                  ))}
                </div>
              </div>
            );
          })}

          <div className="pt-4 border-t border-border-medium">
            <Typography variant="strong" className="mb-2 block text-warning">
              Regenerate bracket
            </Typography>
            <Text className="text-text-secondary text-sm mb-3 block">
              Choose which teams qualify from the groups, then regenerate the bracket from your selection. Existing knockout matches and scores will be replaced.
            </Text>
            <Button
              variant="warning"
              size="medium"
              onClick={onRegenerateConfirm}
              disabled={regenerating}
            >
              {regenerating ? 'Regenerating...' : '🔄 Regenerate Knockout Matches'}
            </Button>
          </div>
        </div>
      </ModalContent>
    </Modal>
  );
};

export default EditKnockoutMatchupsModal;
